import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  Mail,
  Phone,
  CheckCircle2,
  FileText,
  Cpu,
  Layers,
  ShieldAlert,
  Download,
  Share2,
  Eye,
  Maximize2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCatalog } from '../../hooks/use-catalog';
import { usePdfViewer } from '../../hooks/use-pdf-viewer';
import { ProductEntity } from '../../../domain/entities/product.entity';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ProductImageZoom } from '../../components/ui/product-image-zoom';
import { ProductGalleryModal } from '../../components/ui/product-gallery-modal';
import { SeoHead } from '../../components/seo/seo-head';

const getUniqueImages = (images?: string[], fallbackUrl?: string): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  const rawList = images && images.length > 0 ? images : (fallbackUrl ? [fallbackUrl] : []);

  for (const url of rawList) {
    if (!url) continue;
    const clean = url.trim();
    const key = clean.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(clean);
    }
  }
  return result.length > 0 ? result : (fallbackUrl ? [fallbackUrl] : []);
};

export const ProductoDetallePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getProductBySlugUseCase, trackProductViewUseCase, trackProductInquiryUseCase } = useCatalog();
  const { openPdf } = usePdfViewer();

  const [product, setProduct] = useState<ProductEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'specs' | 'datasheet' | 'desc' | 'interfaces' | 'drivers'>('specs');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const trackedProductRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchProduct = async () => {
      if (!slug) return;
      setLoading(true);
      setSelectedImageIndex(0);
      try {
        const item = await getProductBySlugUseCase.execute(slug);
        if (!isMounted) return;
        setProduct(item);
        if (item?.id && trackedProductRef.current !== item.id) {
          trackedProductRef.current = item.id;
          trackProductViewUseCase.execute(item.id).catch(() => {});
        }
      } catch (err) {
        console.error('Error cargando producto:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchProduct();
    return () => {
      isMounted = false;
    };
  }, [slug, getProductBySlugUseCase, trackProductViewUseCase]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 animate-pulse space-y-8">
        <div className="h-6 w-64 bg-neutral-200 dark:bg-neutral-800 rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="h-96 bg-neutral-200 dark:bg-neutral-800 rounded-2xl" />
          <div className="space-y-4">
            <div className="h-10 w-3/4 bg-neutral-200 dark:bg-neutral-800 rounded" />
            <div className="h-24 bg-neutral-200 dark:bg-neutral-800 rounded" />
            <div className="h-48 bg-neutral-200 dark:bg-neutral-800 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center space-y-4">
        <ShieldAlert className="w-12 h-12 text-brand-primary mx-auto" />
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">
          Equipo de hardware no encontrado
        </h2>
        <p className="text-sm text-neutral-500">
          El modelo consultado no se encuentra en el catálogo vigente o el enlace ha caducado.
        </p>
        <Button variant="primary" onClick={() => navigate('/catalogo')}>
          Volver al Catálogo
        </Button>
      </div>
    );
  }

  const uniqueImages = getUniqueImages(product.images, product.imageUrl);

  const whatsappMessage = encodeURIComponent(
    `Hola BARTECH, me interesa cotizar y consultar especificaciones del equipo: ${product.name}${product.model ? ` (Modelo: ${product.model})` : ''} — Marca: ${product.brand}.`
  );

  const handleCopyShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.info('Enlace de ficha técnica copiado al portapapeles');
  };

  const handleOpenDatasheet = () => {
    if (product.datasheetUrl) {
      openPdf({
        url: product.datasheetUrl,
        title: `Ficha Técnica Oficial — ${product.name}`,
        subtitle: `${product.model ? `Modelo: ${product.model} • ` : ''}Fabricante: ${product.brand} • Homologado por BARTECH SpA`,
      });
    } else {
      window.open(
        `https://wa.me/56971380593?text=${encodeURIComponent(
          `Hola BARTECH, quisiera solicitar la Ficha Técnica oficial en PDF del modelo: ${product.name} (${product.brand}).`
        )}`,
        '_blank'
      );
    }
  };

  const seoTitle = `Cotizar ${product.name} ${product.model ? `(${product.model}) ` : ''}${product.brand} | Hardware B2B`;
  const seoDescription =
    product.shortDescription ||
    `Cotiza ${product.name} ${product.brand} en Chile con BARTECH. Distribuidores de hardware para retail, captura de datos y bodegas. Ficha técnica, garantía y soporte técnico oficial.`;
  const seoKeywords = `${product.name}, cotizar ${product.name}, ${product.brand} chile, ${product.categoryName}, hardware retail chile, distribuidores honeywell chile, capturadores pda chile, soporte tecnico ${product.brand}`;
  const seoCanonical = `https://bartech.cl/catalogo/${product.slug}`;
  const seoImage = uniqueImages[0] || product.imageUrl || 'https://bartech.cl/bartech_logo.jpg';

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        '@id': `${seoCanonical}#product`,
        'name': product.name,
        'image': uniqueImages.map((img) => (img.startsWith('http') ? img : `https://bartech.cl${img}`)),
        'description': seoDescription,
        'sku': product.model || product.slug,
        'mpn': product.model || product.slug,
        'brand': {
          '@type': 'Brand',
          'name': product.brand,
        },
        'category': product.categoryName,
        'offers': {
          '@type': 'Offer',
          'url': seoCanonical,
          'priceCurrency': 'CLP',
          'price': '0',
          'priceValidUntil': '2027-12-31',
          'itemCondition': 'https://schema.org/NewCondition',
          'availability': 'https://schema.org/InStock',
          'seller': {
            '@type': 'Organization',
            'name': 'BARTECH - Tecnología e Ingeniería SpA',
            'url': 'https://bartech.cl',
          },
        },
        'additionalProperty': Object.entries(product.specifications || {}).map(([key, val]) => ({
          '@type': 'PropertyValue',
          'name': key,
          'value': String(val),
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${seoCanonical}#breadcrumb`,
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': 'Inicio',
            'item': 'https://bartech.cl',
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': 'Catálogo',
            'item': 'https://bartech.cl/catalogo',
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': product.categoryName || 'Categoría',
            'item': `https://bartech.cl/catalogo?categoria=${product.categoryId}`,
          },
          {
            '@type': 'ListItem',
            'position': 4,
            'name': product.name,
            'item': seoCanonical,
          },
        ],
      },
    ],
  };

  return (
    <div className="max-w-7xl mx-auto px-3.5 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={seoCanonical}
        ogType="product"
        ogImage={seoImage}
        jsonLd={productJsonLd}
      />
      {/* Breadcrumb Técnico */}
      <nav className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-4 sm:mb-6 overflow-x-auto whitespace-nowrap pb-1 no-scrollbar">
        <Link to="/" className="hover:text-brand-primary transition-colors">
          Inicio
        </Link>
        <span>/</span>
        <Link to="/catalogo" className="hover:text-brand-primary transition-colors">
          Catálogo
        </Link>
        <span>/</span>
        <Link
          to={`/catalogo?categoria=${product.categoryId}`}
          className="hover:text-brand-primary transition-colors"
        >
          {product.categoryName}
        </Link>
        <span>/</span>
        <span className="text-neutral-900 dark:text-white font-medium truncate max-w-[200px] sm:max-w-none">
          {product.name}
        </span>
      </nav>

      {/* Main Product Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-10">
        {/* Columna Izquierda: Galería del Hardware con Zoom y Modal */}
        <div className="lg:col-span-6 space-y-3 sm:space-y-4">
          <ProductImageZoom
            src={uniqueImages[selectedImageIndex] || uniqueImages[0] || product.imageUrl}
            alt={`${product.name} - Vista ${selectedImageIndex + 1}`}
            brandBadge={product.brand}
            onImageClick={() => setGalleryModalOpen(true)}
          />

          {/* Tira de Miniaturas del Producto (Solo si hay más de 1 imagen real) */}
          {uniqueImages.length > 1 && (
            <div className="flex items-center gap-2 sm:gap-2.5 overflow-x-auto pb-1.5 pt-0.5 no-scrollbar">
              {uniqueImages.map((img, idx) => {
                const isActive = idx === selectedImageIndex;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 cursor-pointer bg-white dark:bg-neutral-800 p-1.5 ${
                      isActive
                        ? 'border-brand-primary ring-2 ring-brand-primary/30 shadow-md scale-102'
                        : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-400 opacity-75 hover:opacity-100'
                    }`}
                    aria-label={`Ver foto ${idx + 1}`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} - Miniatura ${idx + 1}`}
                      className="w-full h-full object-contain rounded-lg"
                    />
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-neutral-500 dark:text-neutral-400 px-1">
            <span className="truncate">
              {product.model
                ? <>Modelo de fábrica: <strong className="text-neutral-800 dark:text-neutral-200">{product.model}</strong></>
                : <>Fabricante: <strong className="text-neutral-800 dark:text-neutral-200">{product.brand}</strong></>
              }
            </span>
            <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
              {uniqueImages.length > 1 && (
                <button
                  type="button"
                  onClick={() => setGalleryModalOpen(true)}
                  className="inline-flex items-center gap-1.5 text-brand-primary font-semibold hover:underline cursor-pointer"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span>Ver galería ({uniqueImages.length})</span>
                </button>
              )}
              <button
                type="button"
                onClick={handleCopyShare}
                className="inline-flex items-center gap-1.5 hover:text-brand-primary transition-colors cursor-pointer"
              >
                <Share2 className="w-3.5 h-3.5" />
                <span>Compartir</span>
              </button>
            </div>
          </div>
        </div>

        {/* Columna Derecha: Información y Conversión */}
        <div className="lg:col-span-6 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
                {product.categoryName}
              </span>
              <span className="text-neutral-300 dark:text-neutral-700">•</span>
              <span className="text-xs text-neutral-700 dark:text-neutral-300 font-bold flex items-center gap-1.5 bg-neutral-100 dark:bg-neutral-800 px-3 py-0.5 rounded-full border border-neutral-200 dark:border-neutral-700 shadow-xs">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#33CC00]" />
                <span>Garantía Oficial 1 Año</span>
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-black text-neutral-900 dark:text-white tracking-tight leading-tight">
              {product.name}
            </h1>

            <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-3 leading-relaxed">
              {product.shortDescription}
            </p>
          </div>

          {/* Caja de Conversión y Contacto Directo */}
          <div className="bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700 rounded-2xl p-5 sm:p-6 space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-neutral-900 dark:text-white">
                ¿Interesado en este equipo para tu negocio, proyecto o empresa?
              </h3>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                Te entregamos asesoría directa, cotización formal, soporte oficial y despacho rápido a todo el país.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`https://wa.me/56971380593?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (product?.id) {
                    trackProductInquiryUseCase.execute(product.id, 'product_detail').catch(() => {});
                  }
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#33CC00] hover:bg-[#2EB800] text-white font-bold text-sm shadow-[0_4px_15px_rgba(51,204,0,0.35)] transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Consultar por WhatsApp</span>
              </a>

              <Link to="/contacto" className="flex-1 inline-flex">
                <Button variant="primary" size="lg" className="w-full">
                  <Mail className="w-4 h-4" />
                  <span>Solicitar Información</span>
                </Button>
              </Link>
            </div>

            <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700/60 flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
              <span className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-brand-primary" />
                Mesa central: <strong>+56 2 2923 8600</strong>
              </span>
              <span className="flex items-center gap-1 font-semibold text-[#1a7f00] dark:text-[#33CC00]">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Garantía Oficial 1 Año
              </span>
            </div>
          </div>

          {/* Tarjeta de Ficha Técnica / Datasheet Oficial */}
          <div className="p-4 rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3.5 shadow-sm">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-950/40 text-brand-primary flex items-center justify-center flex-shrink-0 border border-brand-200 dark:border-brand-800">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm font-bold text-neutral-900 dark:text-white truncate">
                    {product.datasheetUrl ? 'Ficha Técnica Oficial (PDF)' : 'Ficha Técnica y Homologación'}
                  </span>
                  <Badge variant={product.datasheetUrl ? 'brand' : 'neutral'} size="sm">
                    {product.datasheetUrl ? 'Datasheet' : 'Consultar'}
                  </Badge>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {product.datasheetUrl
                    ? 'Visualiza especificaciones y diagramas técnicos en pantalla'
                    : 'Solicita el documento técnico oficial y manuales a nuestros ingenieros'}
                </p>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleOpenDatasheet}
              className="w-full sm:w-auto font-bold flex items-center justify-center gap-2 cursor-pointer hover:border-brand-primary hover:text-brand-primary flex-shrink-0"
            >
              {product.datasheetUrl ? (
                <>
                  <Eye className="w-4 h-4 text-brand-primary" />
                  <span>Ver Ficha Técnica</span>
                </>
              ) : (
                <>
                  <MessageCircle className="w-4 h-4 text-brand-primary" />
                  <span>Solicitar Ficha (PDF)</span>
                </>
              )}
            </Button>
          </div>

          {/* Resumen Rápido de Interfaces (solo si existen en el producto) */}
          {product.interfaces && product.interfaces.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Interfaces de Conexión Soportadas:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {product.interfaces.map((iface) => (
                  <span
                    key={iface}
                    className="px-2.5 py-1 rounded-md text-xs font-medium bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700"
                  >
                    {iface}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs Técnicos Inferiores */}
      <div className="mt-12 sm:mt-16 space-y-6">
        {/* Tab Headers */}
        <div className="flex items-center gap-2 border-b border-neutral-200 dark:border-neutral-800 overflow-x-auto pb-px">
          {[
            { id: 'specs', label: 'Especificaciones Técnicas', icon: Cpu },
            { id: 'datasheet', label: 'Ficha Técnica Oficial (PDF)', icon: FileText },
            { id: 'desc', label: 'Descripción de Ingeniería', icon: Layers },
            { id: 'interfaces', label: 'Interfaces & Puertos', icon: Cpu },
            { id: 'drivers', label: 'Drivers & Respaldo', icon: Download },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors cursor-pointer ${
                  isActive
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-6 sm:p-8">
          {activeTab === 'specs' && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-neutral-900 dark:text-white">
                Ficha Técnica y Parámetros Operativos
              </h3>
              {Object.keys(product.specifications).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div
                      key={key}
                      className="p-3.5 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-100 dark:border-neutral-800 flex flex-col sm:flex-row sm:items-center justify-between gap-1"
                    >
                      <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                        {key}
                      </span>
                      <span className="text-xs font-bold text-neutral-900 dark:text-white text-right">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 px-4 text-center space-y-3 bg-neutral-50 dark:bg-neutral-800/30 rounded-xl border border-neutral-200/60 dark:border-neutral-800">
                  <Cpu className="w-8 h-8 text-neutral-400 mx-auto" />
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-neutral-800 dark:text-neutral-200">
                      Especificaciones técnicas en proceso de homologación
                    </p>
                    <p className="text-xs text-neutral-500 max-w-md mx-auto">
                      Este producto no cuenta con especificaciones digitalizadas en el catálogo. Si necesitas asesoría técnica o dimensiones, contáctanos directamente.
                    </p>
                  </div>
                  <a
                    href={`https://wa.me/56971380593?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-primary hover:underline pt-1"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>Consultar especificaciones por WhatsApp</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {activeTab === 'datasheet' && (
            <div className="space-y-6">
              {product.datasheetUrl ? (
                <>
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-4 border-b border-neutral-200 dark:border-neutral-800">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="brand" size="sm">Documento Oficial de Fábrica</Badge>
                        <span className="text-xs text-neutral-400 font-mono">PDF Homologado</span>
                      </div>
                      <h3 className="text-lg font-bold text-neutral-900 dark:text-white">
                        Ficha Técnica y Datasheet Oficial — {product.name}
                      </h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                        Especificaciones certificadas y manual de integración por el laboratorio SmartServ de BARTECH.
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                      <Button
                        variant="primary"
                        size="md"
                        onClick={handleOpenDatasheet}
                        className="w-full sm:w-auto shadow-sm flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Visualizar Ficha en Pantalla</span>
                      </Button>

                      <a
                        href={product.datasheetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-800 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-700 transition-colors"
                        title="Abrir en pestaña nueva del navegador sin descargar"
                      >
                        <Maximize2 className="w-3.5 h-3.5" />
                        <span>Pantalla Completa</span>
                      </a>
                    </div>
                  </div>

                  {/* Vista Previa Embebida en la Ficha Técnica */}
                  <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden bg-neutral-100 dark:bg-neutral-950 h-[480px] relative">
                    <iframe
                      src={`${product.datasheetUrl}#toolbar=0&view=FitH`}
                      title={`Ficha Técnica ${product.name}`}
                      className="w-full h-full border-0"
                    />
                    <div className="absolute bottom-3 left-3 right-3 p-3 rounded-xl bg-neutral-900/90 text-white text-xs flex flex-col sm:flex-row items-center justify-between gap-2 shadow-lg backdrop-blur-sm">
                      <span>Visualización interactiva sin descargas obligatorias para el cliente.</span>
                      <button
                        type="button"
                        onClick={handleOpenDatasheet}
                        className="text-brand-primary font-bold hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Abrir en Visor Completo</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="py-8 px-6 text-center space-y-4 max-w-xl mx-auto">
                  <div className="w-14 h-14 rounded-2xl bg-neutral-100 dark:bg-neutral-800 text-neutral-500 flex items-center justify-center mx-auto border border-neutral-200 dark:border-neutral-700">
                    <FileText className="w-7 h-7 text-brand-primary" />
                  </div>
                  <div className="space-y-1.5">
                    <h3 className="text-lg font-bold text-neutral-900 dark:text-white">
                      Ficha Técnica en Proceso de Homologación
                    </h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                      El documento técnico oficial para <strong>{product.name}</strong> está disponible para descarga inmediata a través de nuestro equipo de ingeniería técnica y comercial.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                    <a
                      href={`https://wa.me/56971380593?text=${encodeURIComponent(
                        `Hola BARTECH, solicito la ficha técnica en PDF del modelo: ${product.name} (${product.brand})`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold text-xs shadow-sm transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>Solicitar PDF por WhatsApp</span>
                    </a>
                    <Link to="/contacto" className="w-full sm:w-auto inline-flex">
                      <Button variant="outline" size="md" className="w-full">
                        <Mail className="w-4 h-4" />
                        <span>Contactar Soporte</span>
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'desc' && (
            <div className="space-y-4 max-w-3xl leading-relaxed text-sm text-neutral-700 dark:text-neutral-300">
              <h3 className="text-base font-bold text-neutral-900 dark:text-white">
                Descripción Funcional para Entornos Comerciales
              </h3>
              <p>{product.longDescription}</p>
              <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-950/20 border border-brand-200 dark:border-brand-900/40 text-xs text-neutral-700 dark:text-neutral-300 space-y-2">
                <span className="font-bold text-brand-primary block">
                  Recomendación Técnica de los Ingenieros BARTECH:
                </span>
                <p>
                  Recomendado para locales comerciales con ciclo de operación continua mayor a 12 horas diarias. Compatible con ambientes industriales con presencia de polvo o humedad moderada.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'interfaces' && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-neutral-900 dark:text-white">
                Conectores y Puertos de Comunicación
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.interfaces.map((iface) => (
                  <li
                    key={iface}
                    className="flex items-center gap-2.5 p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800 text-xs font-medium text-neutral-800 dark:text-neutral-200"
                  >
                    <CheckCircle2 className="w-4 h-4 text-brand-primary flex-shrink-0" />
                    <span>{iface}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'drivers' && (
            <div className="space-y-4 max-w-2xl text-sm">
              <h3 className="text-base font-bold text-neutral-900 dark:text-white">
                Drivers, Firmware y Soporte Técnico Homologado
              </h3>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">
                BARTECH provee a sus clientes los controladores oficiales, SDKs de desarrollo y guías de configuración para integración directa.
              </p>
              <div className="space-y-2 pt-2">
                <div className="p-3.5 rounded-lg border border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold block text-neutral-900 dark:text-white">
                      Driver USB / Virtual COM Port para Windows 10/11
                    </span>
                    <span className="text-[11px] text-neutral-500">Firmware certificado de fábrica</span>
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => toast.success('Descarga de drivers solicitada al servidor')}>
                    <Download className="w-3.5 h-3.5" />
                    <span>Descargar</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal / Lightbox de Galería de Fotos del Producto */}
      <ProductGalleryModal
        isOpen={galleryModalOpen}
        onClose={() => setGalleryModalOpen(false)}
        images={uniqueImages}
        initialIndex={selectedImageIndex}
        productName={product.name}
        productModel={product.model}
        brand={product.brand}
      />
    </div>
  );
};
